import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { AlertOctagon, AlertTriangle, CalendarClock, CalendarCheck } from "lucide-react";

function daysUntil(d) {
  if (!d) return null;
  const t = new Date(d); if (isNaN(t.getTime())) return null;
  const today = new Date(); today.setHours(0,0,0,0); t.setHours(0,0,0,0);
  return Math.round((t - today) / 86400000);
}

function ExpiryCell({ date }) {
  if (!date) return <span className="text-slate-400 text-xs">—</span>;
  const days = daysUntil(date);
  const iso = new Date(date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  const cls = days == null ? "bg-slate-50 text-slate-700 border-slate-200"
    : days < 0 ? "bg-rose-100 text-rose-900 border-rose-300 font-semibold"
    : days <= 14 ? "bg-rose-50 text-rose-800 border-rose-200 font-semibold"
    : days <= 30 ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-emerald-50 text-emerald-800 border-emerald-200";
  const badge = days == null ? "" : days < 0 ? `EXPIRED ${Math.abs(days)}d ago` : days === 0 ? "TODAY" : `${days}d left`;
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`stage-badge ${cls}`}>{iso}</span>
      <span className={`text-[10px] uppercase tracking-wide ${days!=null && (days<0||days<=14)?"text-rose-700":days!=null && days<=30?"text-amber-700":"text-slate-500"}`}>{badge}</span>
    </div>
  );
}

export default function B2CApplications() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({});
  const [counts, setCounts] = useState({ expired: 0, d7: 0, d14: 0, d30: 0 });
  const nav = useNavigate();
  const load = async () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([_,v]) => v && v !== "all"));
    const [r, m, c] = await Promise.all([
      api.get(`/b2c/applications?${qs}`),
      api.get("/b2c/meta"),
      api.get("/b2c/expiry-counts"),
    ]);
    setRows(r.data); setMeta(m.data); setCounts(c.data);
  };
  useEffect(() => { load(); }, [filters]); // eslint-disable-line
  if (!meta) return <div>Loading…</div>;
  const officers = meta.users.filter(u => ["admissions_officer","admin","manager"].includes(u.role));
  const activeBucket = filters.expiry_bucket;
  const toggleBucket = (b) => setFilters(f => ({ ...f, expiry_bucket: f.expiry_bucket === b ? undefined : b }));

  const UrgencyTile = ({ id, label, value, icon: Icon, tone, testid }) => {
    const active = activeBucket === id;
    const tones = {
      rose:   { base: "border-rose-200",   text: "text-rose-700",   bg: "bg-rose-50",   ring: "ring-rose-400",   icon: "text-rose-500" },
      amber:  { base: "border-amber-200",  text: "text-amber-700",  bg: "bg-amber-50",  ring: "ring-amber-400",  icon: "text-amber-500" },
      orange: { base: "border-orange-200", text: "text-orange-700", bg: "bg-orange-50", ring: "ring-orange-400", icon: "text-orange-500" },
      slate:  { base: "border-slate-200",  text: "text-slate-700",  bg: "bg-white",     ring: "ring-slate-400",  icon: "text-slate-500" },
    }[tone] || {};
    return (
      <button
        data-testid={testid}
        onClick={() => toggleBucket(id)}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${tones.base} ${active ? `${tones.bg} ring-2 ${tones.ring}` : "bg-white hover:shadow-sm"} transition-all text-left`}
      >
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones.bg} ${tones.icon}`}><Icon className="w-4 h-4"/></div>
        <div>
          <div className={`text-[10px] uppercase tracking-widest font-semibold ${tones.text}`}>{label}</div>
          <div className="heading-font text-2xl font-bold text-slate-900 leading-none mt-0.5">{value}</div>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-5">
      <div><h1 className="heading-font text-3xl font-bold text-slate-900">B2C Applications</h1><p className="text-sm text-slate-500 mt-1">{rows.length} record{rows.length!==1?"s":""}{activeBucket ? " · filtered by visa urgency" : ""}</p></div>

      {/* Urgency tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <UrgencyTile id="expired" label="Expired" value={counts.expired} icon={AlertOctagon} tone="rose" testid="b2c-urg-expired" />
        <UrgencyTile id="7d" label="Expires in 0-7 days" value={counts.d7} icon={AlertTriangle} tone="rose" testid="b2c-urg-7d" />
        <UrgencyTile id="14d" label="Expires in 8-14 days" value={counts.d14} icon={CalendarClock} tone="amber" testid="b2c-urg-14d" />
        <UrgencyTile id="30d" label="Expires in 15-30 days" value={counts.d30} icon={CalendarCheck} tone="orange" testid="b2c-urg-30d" />
      </div>

      <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-xl border border-slate-200">
        <Input placeholder="Search…" value={filters.q||""} onChange={e=>setFilters({...filters, q:e.target.value})} data-testid="b2c-search" className="h-9 w-64 bg-white"/>
        <Select value={filters.stage||"all"} onValueChange={v=>setFilters({...filters, stage:v})}>
          <SelectTrigger className="h-9 w-56 bg-white" data-testid="b2c-stage-filter"><SelectValue placeholder="Stage"/></SelectTrigger>
          <SelectContent><SelectItem value="all">All Stages</SelectItem>{meta.stages.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.college_id||"all"} onValueChange={v=>setFilters({...filters, college_id:v})}>
          <SelectTrigger className="h-9 w-56 bg-white"><SelectValue placeholder="College"/></SelectTrigger>
          <SelectContent><SelectItem value="all">All Colleges</SelectItem>{meta.colleges.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.intake_id||"all"} onValueChange={v=>setFilters({...filters, intake_id:v})}>
          <SelectTrigger className="h-9 w-44 bg-white"><SelectValue placeholder="Intake"/></SelectTrigger>
          <SelectContent><SelectItem value="all">All Intakes</SelectItem>{meta.intakes.map(i=><SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.admission_officer_id||"all"} onValueChange={v=>setFilters({...filters, admission_officer_id:v})}>
          <SelectTrigger className="h-9 w-56 bg-white" data-testid="b2c-officer-filter"><SelectValue placeholder="Admission Officer"/></SelectTrigger>
          <SelectContent><SelectItem value="all">All Officers</SelectItem>{officers.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">App ID</th>
              <th className="text-left px-4 py-3">Student</th>
              <th className="text-left px-4 py-3">College</th>
              <th className="text-left px-4 py-3">Intake</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-left px-4 py-3">Filing</th>
              <th className="text-left px-4 py-3">Visa Expiry</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(a=>(
              <tr key={a.id} data-testid={`b2c-row-${a.application_id}`} onClick={()=>nav(`/b2c/applications/${a.id}`)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3 mono text-xs">{a.application_id}</td>
                <td className="px-4 py-3 font-medium">{a.student?.full_name}<div className="text-xs text-slate-500">{a.student?.email}</div></td>
                <td className="px-4 py-3">
                  <span title={a.college?.name} className="mono text-xs font-semibold text-slate-800">{a.college?.code || a.college?.name || "—"}</span>
                  <div className="text-xs text-slate-500 truncate max-w-[180px]" title={a.program?.name}>{a.program?.name}</div>
                </td>
                <td className="px-4 py-3">{a.intake?.name}</td>
                <td className="px-4 py-3"><span className="stage-badge bg-blue-50 text-blue-900 border-blue-300">{a.stage}{a.stage === "Deferred" && a.deferred_to ? ` → ${a.deferred_to}` : ""}</span></td>
                <td className="px-4 py-3">{a.filing_started ? <span className="stage-badge bg-purple-50 text-purple-900 border-purple-300">{a.filing_status}</span> : <span className="text-slate-400 text-xs">—</span>} {a.pal_pending && <span className="stage-badge bg-amber-50 text-amber-900 border-amber-300 ml-1">PAL pending</span>}</td>
                <td className="px-4 py-3" data-testid={`b2c-expiry-${a.application_id}`}><ExpiryCell date={a.visa_expiry} /></td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No applications.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
